import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { supabase } from "./supabase";
import { SiteClientConnector } from "./SiteClientConnector";
import { RecipientCoverageMap } from "./RecipientCoverageMap";
import type { PortfolioDirectoryData } from "./types";
import { SortHeader, useSortedRows } from "./table-sort";

type Rule = { client: string; emails: string[]; percentage: number; paused: boolean };
type Route = {
  property_id: number;
  domain: string;
  name: string;
  mode: string;
  active_recipients: string[];
  draft_rules: Rule[];
  revision: number | null;
  editable: boolean;
  notification_counts: Record<string, number>;
};
type Receiver = {
  receiver_key: string;
  property_id: number | null;
  domain: string | null;
  landing_path: string | null;
  campaign_label: string;
  market: string;
  contact_name: string;
  company_name: string | null;
  emails: string[];
  phones: string[];
  percentage: number | null;
  mapping_status: "confirmed_by_url" | "confirmed_by_user" | "mapped_from_campaign" | "inferred_needs_confirmation" | "destination_required" | "protected_no_change";
  mapping_evidence: string;
  route_blocker: string | null;
  notes: string | null;
  delivery_state: "draft_only" | "active_with_internal_copy";
};
/** What each sortable receiver column sorts by. */
const receiverColumns: Record<string, (r: Receiver) => unknown> = {
  website: (r) => r.domain, campaign: (r) => r.campaign_label, client: (r) => r.company_name || r.contact_name,
  share: (r) => r.percentage, status: (r) => r.mapping_status,
};

type ReceiverSummary = {
  total: number;
  confirmed: number;
  user_confirmed: number;
  campaign_mapped: number;
  needs_confirmation: number;
  destination_required: number;
  protected: number;
  active: number;
  with_blockers: number;
};

const emptySummary: ReceiverSummary = {
  total: 0,
  confirmed: 0,
  user_confirmed: 0,
  campaign_mapped: 0,
  needs_confirmation: 0,
  destination_required: 0,
  protected: 0,
  active: 0,
  with_blockers: 0,
};

const statusLabels: Record<Receiver["mapping_status"], string> = {
  confirmed_by_url: "URL confirmed",
  confirmed_by_user: "Owner confirmed",
  mapped_from_campaign: "Campaign mapped",
  inferred_needs_confirmation: "Confirm destination",
  destination_required: "URL needed",
  protected_no_change: "Protected · unchanged",
};

export function LeadRouting({directory}: {directory: PortfolioDirectoryData | null}) {
  const [loadedAt,setLoadedAt] = useState('');
  const [rows, setRows] = useState<Route[]>([]);
  const [receivers, setReceivers] = useState<Receiver[]>([]);
  const [summary, setSummary] = useState<ReceiverSummary>(emptySummary);
  const [selected, setSelected] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [receiverQuery, setReceiverQuery] = useState("");
  const [receiverStatus, setReceiverStatus] = useState<Receiver["mapping_status"] | "all">("all");
  const [rules, setRules] = useState<Rule[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [externalRoutingEnabled, setExternalRoutingEnabled] = useState(false);
  const route = rows.find((row) => row.property_id === selected);

  async function load() {
    setLoading(true);
    const result = await supabase.rpc("dashboard_lead_routing");
    if (result.error) setMessage(result.error.message);
    else {
      const next = (result.data?.rows || []) as Route[];
      setRows(next);
      setReceivers((result.data?.receiver_matrix || []) as Receiver[]);
      setSummary((result.data?.receiver_summary || emptySummary) as ReceiverSummary);
      setExternalRoutingEnabled(Boolean(result.data?.external_routing_enabled));
      setLoadedAt(result.data?.generated_at || '');
      setSelected((current) => current || next.find((row) => row.editable)?.property_id || null);
    }
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    setRules(route?.draft_rules || []);
    setDirty(false);
    setMessage("");
  }, [selected, route?.revision]);

  const filtered = rows.filter((row) => `${row.domain} ${row.name}`.toLowerCase().includes(query.toLowerCase()));
  const filteredReceivers = receivers.filter((receiver) => {
    const haystack = [receiver.domain, receiver.landing_path, receiver.campaign_label, receiver.market, receiver.contact_name, receiver.company_name, ...receiver.emails]
      .filter(Boolean).join(" ").toLowerCase();
    return (receiverStatus === "all" || receiver.mapping_status === receiverStatus)
      && haystack.includes(receiverQuery.trim().toLowerCase());
  });
  const receiverSort = useSortedRows(filteredReceivers, receiverColumns, { key: "website", direction: "asc" }, { share: "desc" });
  const total = rules.filter((rule) => !rule.paused).reduce((sum, rule) => sum + rule.percentage, 0);
  const preview = useMemo(() => {
    if (total !== 100) return [];
    const eligible = rules.filter((rule) => !rule.paused && rule.percentage > 0);
    const scores = eligible.map(() => 0);
    const cycle: string[] = [];
    for (let n = 0; n < 20; n++) {
      let best = 0;
      eligible.forEach((rule, index) => {
        scores[index] = (scores[index] || 0) + rule.percentage;
        if (scores[index]! > scores[best]!) best = index;
      });
      if (!eligible[best]) break;
      scores[best]! -= 100;
      cycle.push(eligible[best]!.client || "Unnamed client");
    }
    return cycle;
  }, [rules, total]);

  function update(index: number, patch: Partial<Rule>) {
    setRules((current) => current.map((rule, currentIndex) => currentIndex === index ? { ...rule, ...patch } : rule));
    setDirty(true);
    setMessage("");
  }

  async function save() {
    if (!route) return;
    setSaving(true);
    setMessage("");
    const result = await supabase.rpc("dashboard_save_lead_routing_draft", {
      p_property_id: route.property_id,
      p_rules: rules,
      p_revision: route.revision,
    });
    if (result.error) setMessage(result.error.message);
    else {
      setRows((current) => current.map((row) => row.property_id === route.property_id ? { ...row, ...result.data } : row));
      setDirty(false);
      setMessage("Draft saved. Saving a draft does not change the active recipient route.");
    }
    setSaving(false);
  }

  return <section className="section lead-routing">
    <div className="section__head"><div><h2>Where every form lead goes</h2><p>{summary.active} routes are active for client delivery with <strong>justin@aryocg.com</strong> retained as a hidden monitoring copy. Unactivated sites remain Justin-only. Calls and CallRail routing are unchanged.</p></div><button className="button button--quiet" disabled={loading || dirty} onClick={() => void load()}><RefreshCw />Refresh routes</button></div>
    <p className="routing-boundary">{externalRoutingEnabled ? "Per-site client routing is active only where the table says Live + monitored." : "External client routing is not active."} Draft rules cannot activate a recipient by themselves. Test submissions remain Justin-only, and competitors do not receive one another’s leads.</p>
    {loading && <p role="status">Loading delivery settings…</p>}

    {!loading && loadedAt && <RecipientCoverageMap directory={directory} routes={rows} loadedAt={loadedAt} />}
    {!loading && <SiteClientConnector directory={directory} routes={rows} onSite={(id)=>{if(dirty&&!window.confirm("Discard the unsaved draft for this site?"))return;setSelected(id);document.querySelector('.routing-editor-title')?.scrollIntoView({behavior:'smooth'});}} />}
    <section className="receiver-matrix" aria-labelledby="receiver-matrix-title">
      <div className="receiver-matrix__head"><div><h3 id="receiver-matrix-title">Client receiver matrix</h3><p>Website and landing path matter. A 100% share applies only to the route shown on that row.</p></div><span className="draft-lock">{summary.active} live · monitored</span></div>
      <div className="receiver-stats" aria-label="Receiver matrix totals">
        <div><strong>{summary.total}</strong><span>receiver rows</span></div>
        <div><strong>{summary.confirmed}</strong><span>URL confirmed</span></div>
        <div><strong>{summary.user_confirmed}</strong><span>owner confirmed</span></div>
        <div><strong>{summary.active}</strong><span>live routes</span></div>
        <div><strong>{summary.campaign_mapped}</strong><span>campaign mapped</span></div>
        <div><strong>{summary.needs_confirmation}</strong><span>confirm destination</span></div>
        <div><strong>{summary.destination_required}</strong><span>URL needed</span></div>
        <div><strong>{summary.protected}</strong><span>protected</span></div>
      </div>
      <div className="receiver-filters">
        <label className="search"><Search /><span className="sr-only">Find a receiver</span><input value={receiverQuery} onChange={(event) => setReceiverQuery(event.target.value)} placeholder="Search website, market, client, or email" /></label>
        <label className="compact-filter"><span>Mapping status</span><select value={receiverStatus} onChange={(event) => setReceiverStatus(event.target.value as typeof receiverStatus)}><option value="all">All statuses</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <span>{filteredReceivers.length} shown</span>
      </div>
      <div className="receiver-table-wrap"><table className="receiver-table"><thead><tr><SortHeader label="Website / route" column="website" sort={receiverSort.sort} onSort={receiverSort.toggle} />
        <SortHeader label="Campaign" column="campaign" sort={receiverSort.sort} onSort={receiverSort.toggle} /><SortHeader label="Client" column="client" sort={receiverSort.sort} onSort={receiverSort.toggle} />
        <SortHeader label="Share" column="share" sort={receiverSort.sort} onSort={receiverSort.toggle} /><th>Recipients</th>
        <SortHeader label="Status / issue" column="status" sort={receiverSort.sort} onSort={receiverSort.toggle} /></tr></thead><tbody>
        {receiverSort.sorted.map((receiver) => {
          const destination = receiver.domain ? `https://${receiver.domain}${receiver.landing_path === "/" ? "" : receiver.landing_path || ""}` : null;
          return <tr key={receiver.receiver_key}>
            <td data-label="Website / route"><strong>{receiver.domain || "Website not provided"}</strong><small>{receiver.landing_path || "Destination needed"}</small>{destination && <a href={destination} target="_blank" rel="noreferrer">Open route <ExternalLink /></a>}</td>
            <td data-label="Campaign"><strong>{receiver.campaign_label}</strong><small>{receiver.market}</small></td>
            <td data-label="Client"><strong>{receiver.company_name || receiver.contact_name}</strong><small>{receiver.company_name ? receiver.contact_name : "Contact"}</small><small>{receiver.phones.join(" · ")}</small></td>
            <td data-label="Share"><strong className={receiver.percentage == null ? "share-tbd" : "share-ready"}>{receiver.percentage == null ? "TBD" : `${receiver.percentage}%`}</strong><small>{receiver.delivery_state === "active_with_internal_copy" ? "Live + monitored" : "Draft"}</small></td>
            <td data-label="Recipients">{receiver.emails.map((email) => <a className="receiver-email" href={`mailto:${email}`} key={email}>{email}</a>)}</td>
            <td data-label="Status / issue"><span className={`receiver-status receiver-status--${receiver.mapping_status}`}>{statusLabels[receiver.mapping_status]}</span><small>{receiver.route_blocker || receiver.mapping_evidence}</small>{receiver.notes && <details><summary>Notes</summary><p>{receiver.notes}</p></details>}</td>
          </tr>;
        })}
      </tbody></table></div>
      {!filteredReceivers.length && <p className="empty">No receiver rows match those filters.</p>}
    </section>

    <h3 className="routing-editor-title">Per-site allocation editor</h3>
    <p className="routing-small">The editor below holds future site-level drafts. Active delivery is shown separately and cannot be changed by saving a draft.</p>
    <div className="routing-layout"><aside className="routing-sites"><label className="search"><Search /><span className="sr-only">Find a website</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a website" /></label><p>{filtered.length} websites · {rows.filter((row) => row.editable).length} configured</p><div className="routing-site-list">{filtered.map((row) => <button key={row.property_id} className={selected === row.property_id ? "selected" : ""} onClick={() => { if (dirty && !window.confirm("Discard the unsaved draft for this site?")) return; setSelected(row.property_id); }}><strong>{row.domain}</strong><span>{row.editable ? `${row.mode === "client_with_internal_copy" ? "Client live + Justin monitoring" : "Justin only"} · ${row.draft_rules.length ? "receiver rule saved" : "no client draft"}` : "Unchanged / outside this rollout"}</span>{Boolean(row.notification_counts.failed || row.notification_counts.uncertain) && <span className="form-error">Email needs attention</span>}</button>)}</div></aside>
      <div className="routing-editor">{route && <><h3>{route.domain}</h3><dl className="routing-active"><div><dt>Active recipient</dt><dd>{route.active_recipients.join(", ") || "Not changed in this rollout"}</dd></div><div><dt>Email status since changeover</dt><dd>{route.notification_counts.sent || 0} provider-accepted · {route.notification_counts.pending || 0} queued · {(route.notification_counts.failed || 0) + (route.notification_counts.uncertain || 0)} need attention</dd></div></dl><p className="routing-small">Provider acceptance is not proof of inbox placement. Preview messages are marked [TEST].</p>
        {route.editable ? <><h3>Client allocation draft</h3><p>Add recipients and set the share of leads for each client. Unpaused percentages must total 100.</p>
          {!rules.length && <p className="empty">No clients added. Justin remains the sole recipient.</p>}
          {rules.map((rule, index) => <fieldset className="routing-rule" key={index}><legend>Client {index + 1}</legend><label>Client / company<input value={rule.client} maxLength={150} onChange={(event) => update(index, { client: event.target.value })} /></label><label>Share of leads (%)<input type="number" min={0} max={100} step={1} value={rule.percentage} onChange={(event) => update(index, { percentage: Number(event.target.value) })} /></label><label className="routing-emails">Recipient emails, separated by commas<textarea rows={2} value={rule.emails.join(", ")} onChange={(event) => update(index, { emails: event.target.value.split(",").map((value) => value.trim()) })} placeholder="owner@example.com, office@example.com" /></label><label className="routing-pause"><input type="checkbox" checked={rule.paused} onChange={(event) => update(index, { paused: event.target.checked })} />Pause this client</label><button className="text-button" onClick={() => { setRules((current) => current.filter((_, currentIndex) => currentIndex !== index)); setDirty(true); }}><Trash2 />Remove client</button></fieldset>)}
          <div className="routing-actions"><button className="button button--quiet" disabled={rules.length >= 20} onClick={() => { setRules((current) => [...current, { client: "", emails: [""], percentage: current.length ? 0 : 100, paused: false }]); setDirty(true); }}><Plus />Add client</button><span className={rules.length && total !== 100 ? "form-error" : ""}>Draft allocation: {total}%</span><button className="button button--primary" disabled={saving || !dirty || (rules.length > 0 && total !== 100)} onClick={() => void save()}>{saving ? "Saving…" : "Save draft"}</button></div>
          {preview.length > 0 && <details className="routing-preview"><summary>Preview the next 20 allocations (simulation only)</summary><ol>{preview.map((name, index) => <li key={index}>{name}</li>)}</ol><p>Whole-number shares are balanced over 100 leads. No messages are sent by this simulation.</p></details>}
          <p className="routing-small">Saving or removing a draft client cannot change today’s active recipients. Activation requires a separately verified routing change.</p></> : <p>This property is protected or not part of the eligible preview set. Its routing has not been changed.</p>}
      </>}
        {message && <p role="status" className={message.startsWith("Draft saved") ? "tracking-success" : "form-error"}>{message}</p>}
      </div></div>
  </section>;
}
